import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatLabel } from '@angular/material/form-field';
import { CommonFunctionsService } from '../../../services/common-functions.service';
import { Constants } from '../../../constants/constants';
import { ReviewDetails } from '../../../models/ReviewDetails';
import { Subject, takeUntil } from 'rxjs';
import { ReviewService } from '../../../services/review.service';
import { CommonDataService } from '../../../services/common-data.service';
import { ExportTitleCaseMap } from '../../../models/PdfRelatedModels';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ExportObjectAsFieldValuePDFOptions } from '../../../models/ExportObjectAsFieldValuePDFOptions';
import { SiFormFieldComponent } from '../../Si-Web-Toolkit/si-form-field/si-form-field.component';
import { SiCurrencyFieldComponent } from '../../Si-Web-Toolkit/si-currency-field/si-currency-field.component';
import { SiRadioButtonComponent } from '../../Si-Web-Toolkit/si-radio-button/si-radio-button.component';
import { SiCheckBoxComponent } from '../../Si-Web-Toolkit/si-check-box/si-check-box.component';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { SiDropDownComponent } from '../../Si-Web-Toolkit/si-drop-down/si-drop-down.component';
import { SiTextAreaComponent } from '../../Si-Web-Toolkit/si-text-area/si-text-area.component';
import { MatRadioModule } from '@angular/material/radio';
import { SiDatePickerComponent } from '../../Si-Web-Toolkit/si-date-picker/si-date-picker.component';
import { CostReviewContractualDocumentation } from '../../../models/CostReviewContractualDocumentation';
import { ReviewDetailTabsService } from '../../../lib/review-detail-tabs.service';
import { OptionalWorkflowDirective } from '../../../Directives/optional-workflow-directive';
import { CostReviewDatabaseRecord } from '../../../models/CostReviewDatabaseRecord';
import { TrackChangesDirective } from '../../../Directives/track-changes.directive';
import { SiCheckBoxGroupComponent } from '../../Si-Web-Toolkit/si-check-box-group/si-check-box-group.component';
import { CostReviewEstimateType } from '../../../public-api';

interface SolContractualDocumentationForm {
  originalCompletionDate: FormControl<string>;
  siemensTermsConditionsUsed: FormControl<string>;
  warrantyPeriod: FormControl<number>;
  liquidatedDamagesLiability: FormControl<string>;
  consequentialDamagesInContract: FormControl<string>;
  waiverReason: FormControl<string>;
  fireStopping: FormControl<string>;
  patchingPainting: FormControl<string>;
  demolitionRemoval: FormControl<string>;
  cleanUp: FormControl<string>;
  possibleChangeOrdersIdentified: FormControl<string>;
  performanceBondAmount: FormControl<number>;
  paymentBondAmount: FormControl<number>;
  penetrationRequirements: FormControl<number[]>;
  trainingSpecified: FormControl<string>;
  changeOrderOpportunities: FormControl<string>;
  changeOrderMarkupRestrictions: FormControl<string>;
  specialInsuranceCertification: FormControl<string>;
  liabilityInsurance: FormControl<string>;
  deductionsOffered: FormControl<string>;
  licensesPermits: FormControl<string>;
  specialSafetyRequirements: FormControl<string>;
  testingFatDates: FormControl<string>;
  testingFromDate: FormControl<string>;
  testingToDate: FormControl<string>;
}

@Component({
  selector: 'app-sol-contractual-tab',
  imports: [
    MatLabel,
    CommonModule,
    MatRadioModule,
    TranslatePipe,
    SiFormFieldComponent,
    SiCurrencyFieldComponent,
    SiRadioButtonComponent,
    SiDropDownComponent,
    SiTextAreaComponent,
    SiDatePickerComponent,
    FormsModule,
    ReactiveFormsModule,
    OptionalWorkflowDirective,
    TrackChangesDirective,
    SiCheckBoxGroupComponent,
    SiCheckBoxComponent
  ],
  templateUrl: './sol-contractual-documentation-tab.component.html',
  styleUrl: './sol-contractual-documentation-tab.component.scss'
})
export class SolContractualDocumentationTabComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  constants = Constants;
  costReviewContractualDocumentation: CostReviewContractualDocumentation | undefined = new CostReviewContractualDocumentation();
  business: string = "";
  isSalePriceGreaterThan150: boolean = false;
  @Input() showSectionNotApplicableMsg: boolean = true;
  @Output() disablePdf = new EventEmitter<boolean>();
  isReadOnlyMode: boolean = false;
  liquidatedDamagesLiabilityOptions: Array<{label:string; value:string}> = [];
  consequentialDamagesInContractOptions: Array<{label:string; value:string}> = [];
  waiverReasonOptionsList: Array<{label:string; value:string}> = [];
  costReviewContractualDocumentationForm!: FormGroup<SolContractualDocumentationForm>;
  optionalWorkflowCondition: boolean = false;
  penetrationRequiredList: string[] = ['REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.CORE_DRILLING', 'REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.SLEEVING_PENETRATIONS'];
  isDisabled: boolean = false;

  constructor(private readonly commonDataService: CommonDataService, 
    private readonly commonFunctionsService: CommonFunctionsService, 
    private readonly reviewService: ReviewService, 
    private readonly translate: TranslateService, 
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly reviewDetailsTabService: ReviewDetailTabsService) { }

  ngOnInit(): void {
    this.business = this.reviewService.getBusiness();
     const estimateType = this.reviewService.convertToEstimateType(
      this.reviewDetailsTabService.getOptionalWorkflowAndEstimateType().optionalEstimateType
    );
    this.isSalePriceGreaterThan150 = estimateType === CostReviewEstimateType.EstimateGreater150K;
     this.getReviewLibraryState();
    this.initializeContractualDocumentationForm();
    this.initializeContractualDocumentationData();
    this.optionalWorkflowCondition = this.reviewDetailsTabService.getOptionalWorkflowAndEstimateType()?.optionalWorkflow || false;
    this.commonDataService.triggerTabMethod$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.exportToPdf(this.reviewService.selectedReview, Constants.SOL_CONTRACTUAL_DOCUMENTATION);
      });
    this.sendDisablePdfEvent();
    this.setupReadOnlyMode();
    this.updateFormEditability();
    this.subscribeToReviewFormDataSyncRequest();
  }

  get isContractualDocumentationChecked(): boolean {
    return !!this.costReviewContractualDocumentation?.isContractualDocumentationApplicable;
  }

  get isSectionVisible(): boolean {
    return !!this.costReviewContractualDocumentation || (this.isContractualDocumentationChecked && this.optionalWorkflowCondition);
  }

  get isSectionLocked(): boolean {
    return this.optionalWorkflowCondition && !this.isContractualDocumentationChecked;
  }

  onContractualDocumentationToggle(checked: boolean): void {
    if (!this.costReviewContractualDocumentation) return;
    this.costReviewContractualDocumentation.isContractualDocumentationApplicable = checked;
    this.updateFormEditability();
  }

  private updateFormEditability(): void {
    if (!this.costReviewContractualDocumentationForm) return;
    const lockedByReadOnly = !this.reviewDetailsTabService.getIsLibraryUsedInSQ() && this.isReadOnlyMode;
    if (lockedByReadOnly || this.isSectionLocked) {
      this.costReviewContractualDocumentationForm.disable();
    } else {
      this.costReviewContractualDocumentationForm.enable();
    }
  }

  /** Allows the host app to force this tab to push its latest data without waiting for tab switch/destroy. */
  private subscribeToReviewFormDataSyncRequest(): void {
    this.reviewDetailsTabService.getReviewFormDataSyncRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.pushFormDataToService());
  }

  private setupReadOnlyMode(): void {
    this.commonDataService.libraryReadOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((readOnly: boolean) => {
        const isLibraryUsedInSQ = this.reviewDetailsTabService.getIsLibraryUsedInSQ();
        if (isLibraryUsedInSQ) {
          this.isDisabled = readOnly;
          this.isReadOnlyMode = false;
        } else {
          this.isReadOnlyMode = readOnly;
        }
        this.updateFormEditability();
      });
    this.subscribeToDataRefresh();
  }

  private subscribeToDataRefresh(): void {
    this.reviewService.onDataRefreshed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const isLibraryUsedInSQ = this.reviewDetailsTabService.getIsLibraryUsedInSQ();
        this.isDisabled = isLibraryUsedInSQ ? this.commonDataService.libraryReadOnlyMode : this.isDisabled;
        this.isReadOnlyMode = isLibraryUsedInSQ ? false : this.commonDataService.libraryReadOnlyMode;
        this.updateFormEditability();
      });
  }

  initializeContractualDocumentationForm(){
    this.costReviewContractualDocumentationForm = this.formBuilder.group<SolContractualDocumentationForm>({
      originalCompletionDate: this.formBuilder.control(Constants.EMPTY_STRING),
      siemensTermsConditionsUsed: this.formBuilder.control(Constants.EMPTY_STRING),
      warrantyPeriod: this.formBuilder.control(Constants.ZERO),
      liquidatedDamagesLiability: this.formBuilder.control(Constants.EMPTY_STRING),
      consequentialDamagesInContract: this.formBuilder.control(Constants.EMPTY_STRING),
      waiverReason: this.formBuilder.control(Constants.EMPTY_STRING),
      fireStopping: this.formBuilder.control(Constants.EMPTY_STRING),
      patchingPainting: this.formBuilder.control(Constants.EMPTY_STRING),
      demolitionRemoval: this.formBuilder.control(Constants.EMPTY_STRING),
      cleanUp: this.formBuilder.control(Constants.EMPTY_STRING),
      possibleChangeOrdersIdentified: this.formBuilder.control(Constants.EMPTY_STRING),
      performanceBondAmount: this.formBuilder.control(Constants.ZERO),
      paymentBondAmount: this.formBuilder.control(Constants.ZERO),
      penetrationRequirements: this.formBuilder.control<number[]>([]),
      trainingSpecified: this.formBuilder.control(Constants.EMPTY_STRING),
      changeOrderOpportunities: this.formBuilder.control(Constants.EMPTY_STRING),
      changeOrderMarkupRestrictions: this.formBuilder.control(Constants.EMPTY_STRING),
      specialInsuranceCertification: this.formBuilder.control(Constants.EMPTY_STRING),
      liabilityInsurance: this.formBuilder.control(Constants.EMPTY_STRING),
      deductionsOffered: this.formBuilder.control(Constants.EMPTY_STRING),
      licensesPermits: this.formBuilder.control(Constants.EMPTY_STRING),
      specialSafetyRequirements: this.formBuilder.control(Constants.EMPTY_STRING),
      testingFatDates: this.formBuilder.control(Constants.EMPTY_STRING),
      testingFromDate: this.formBuilder.control(Constants.EMPTY_STRING),
      testingToDate: this.formBuilder.control(Constants.EMPTY_STRING),
    });
  }

  sendDisablePdfEvent() {
    this.disablePdf.emit(false);
  }

  ngOnDestroy() {
    this.pushFormDataToService();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private pushFormDataToService(): void {
    if (!this.costReviewContractualDocumentationForm || !this.costReviewContractualDocumentation) return;
    const formData = this.costReviewContractualDocumentationForm.getRawValue();

    // Parameter lookup fields
    this.costReviewContractualDocumentation.siemensTermsConditionsUsed = this.getParameterCode(Constants.YES_NO, formData.siemensTermsConditionsUsed, this.costReviewContractualDocumentation.siemensTermsConditionsUsed);
    this.costReviewContractualDocumentation.liquidatedDamagesLiability = this.getParameterCode(Constants.LIQUIDATED_DAMAGES_LIABILITY, formData.liquidatedDamagesLiability, this.costReviewContractualDocumentation.liquidatedDamagesLiability);
    this.costReviewContractualDocumentation.consequentialDamagesInContract = this.getParameterCode(Constants.CONSEQUENTIAL_DAMAGES_IN_CONTRACT, formData.consequentialDamagesInContract, this.costReviewContractualDocumentation.consequentialDamagesInContract);
    this.costReviewContractualDocumentation.waiverReason = this.getParameterCode(Constants.WAIVER_REASON, formData.waiverReason, this.costReviewContractualDocumentation.waiverReason);
    this.costReviewContractualDocumentation.fireStopping = this.getParameterCode(Constants.SIEMENS_OTHER_SUBCONTRACTOR, formData.fireStopping, this.costReviewContractualDocumentation.fireStopping);
    this.costReviewContractualDocumentation.patchingPainting = this.getParameterCode(Constants.SIEMENS_OTHER, formData.patchingPainting, this.costReviewContractualDocumentation.patchingPainting);
    this.costReviewContractualDocumentation.demolitionRemoval = this.getParameterCode(Constants.SIEMENS_OTHER, formData.demolitionRemoval, this.costReviewContractualDocumentation.demolitionRemoval);
    this.costReviewContractualDocumentation.cleanUp = this.getParameterCode(Constants.SIEMENS_OTHER, formData.cleanUp, this.costReviewContractualDocumentation.cleanUp);
    this.costReviewContractualDocumentation.specialInsuranceCertification = this.getParameterCode(Constants.YES_NO, formData.specialInsuranceCertification, this.costReviewContractualDocumentation.specialInsuranceCertification);
    this.costReviewContractualDocumentation.trainingSpecified = this.getParameterCode(Constants.YES_NO, formData.trainingSpecified, this.costReviewContractualDocumentation.trainingSpecified);
    this.costReviewContractualDocumentation.changeOrderMarkupRestrictions = this.getParameterCode(Constants.YES_NO, formData.changeOrderMarkupRestrictions, this.costReviewContractualDocumentation.changeOrderMarkupRestrictions);
    this.costReviewContractualDocumentation.liabilityInsurance = this.getParameterCode(Constants.YES_NO, formData.liabilityInsurance, this.costReviewContractualDocumentation.liabilityInsurance);
    this.costReviewContractualDocumentation.deductionsOffered = this.getParameterCode(Constants.YES_NO, formData.deductionsOffered, this.costReviewContractualDocumentation.deductionsOffered);
    this.costReviewContractualDocumentation.licensesPermits = this.getParameterCode(Constants.YES_NO, formData.licensesPermits, this.costReviewContractualDocumentation.licensesPermits);

    // Direct fields
    this.costReviewContractualDocumentation.warrantyPeriod = this.commonFunctionsService.toNumberOrCurrent(formData.warrantyPeriod, this.costReviewContractualDocumentation.warrantyPeriod);
    this.costReviewContractualDocumentation.possibleChangeOrdersIdentified = formData.possibleChangeOrdersIdentified;
    this.costReviewContractualDocumentation.performanceBondAmount = this.commonFunctionsService.toNumberOrCurrent(formData.performanceBondAmount, this.costReviewContractualDocumentation.performanceBondAmount);
    this.costReviewContractualDocumentation.paymentBondAmount = this.commonFunctionsService.toNumberOrCurrent(formData.paymentBondAmount, this.costReviewContractualDocumentation.paymentBondAmount);
    this.costReviewContractualDocumentation.changeOrderOpportunities = formData.changeOrderOpportunities;
    this.costReviewContractualDocumentation.specialSafetyRequirements = formData.specialSafetyRequirements;

    // Date fields
    if (formData.originalCompletionDate) this.costReviewContractualDocumentation.originalCompletionDate = new Date(formData.originalCompletionDate);
    if (formData.testingFromDate) this.costReviewContractualDocumentation.testingFromDate = new Date(formData.testingFromDate);
    if (formData.testingToDate) this.costReviewContractualDocumentation.testingToDate = new Date(formData.testingToDate);

    // Array field (penetration requirements)
    this.costReviewContractualDocumentation.penetrationRequirements = this.commonFunctionsService.toNumberArray(formData.penetrationRequirements, this.penetrationRequiredList);
  }

  initializeContractualDocumentationData() {
    this.isSalePriceGreaterThan150 = this.reviewDetailsTabService.estimateSellPrice > this.constants.ONE_LAKH_FIFTY_THOUSAND;
    let data: CostReviewDatabaseRecord = this.reviewService.costReviewDetails;
    if (!data?.reviewObject) return;
    this.costReviewContractualDocumentation = data.reviewObject.costReviewContractualDocumentation as any;
    this.liquidatedDamagesLiabilityOptions = this.buildDropDownOptions(Constants.LIQUIDATED_DAMAGES_LIABILITY);
    this.consequentialDamagesInContractOptions = this.buildDropDownOptions(Constants.CONSEQUENTIAL_DAMAGES_IN_CONTRACT);
    this.waiverReasonOptionsList = this.buildDropDownOptions(Constants.WAIVER_REASON);
    this.mapContractualDocumentationData();
  }

  private buildDropDownOptions(key: string) : Array<{label:string; value:string}> {
    const prefix = `${key}_`;
    const options =  [...this.reviewService.costReviewParameters]
      .filter(p => p.parameterID.startsWith(prefix) && p.description?.trim())
      .map(p => ({
        label: p.description,
        value: p.description
      }));
      return [{label: Constants.EMPTY_STRING, value: Constants.EMPTY_STRING}, ...options];
  }

  private formatDateValue(value: Date | string | undefined | null): string {
    if (!value) return Constants.EMPTY_STRING;
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return Constants.EMPTY_STRING;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  mapContractualDocumentationData(){
    this.costReviewContractualDocumentationForm.controls['originalCompletionDate'].setValue(this.formatDateValue(this.costReviewContractualDocumentation?.originalCompletionDate));
    this.costReviewContractualDocumentationForm.controls['siemensTermsConditionsUsed'].setValue(this.getParameterValues(Constants.YES_NO,this.costReviewContractualDocumentation?.siemensTermsConditionsUsed));
    this.costReviewContractualDocumentationForm.controls['warrantyPeriod'].setValue(this.costReviewContractualDocumentation?.warrantyPeriod || Constants.ZERO);
    this.costReviewContractualDocumentationForm.controls['liquidatedDamagesLiability'].setValue(this.getParameterValues(Constants.LIQUIDATED_DAMAGES_LIABILITY,this.costReviewContractualDocumentation?.liquidatedDamagesLiability));
    this.costReviewContractualDocumentationForm.controls['consequentialDamagesInContract'].setValue(this.getParameterValues(Constants.CONSEQUENTIAL_DAMAGES_IN_CONTRACT,this.costReviewContractualDocumentation?.consequentialDamagesInContract));
    this.costReviewContractualDocumentationForm.controls['waiverReason'].setValue(this.getParameterValues(Constants.WAIVER_REASON,this.costReviewContractualDocumentation?.waiverReason));
    this.costReviewContractualDocumentationForm.controls['fireStopping'].setValue(this.getParameterValues(Constants.SIEMENS_OTHER_SUBCONTRACTOR,this.costReviewContractualDocumentation?.fireStopping));
    this.costReviewContractualDocumentationForm.controls['patchingPainting'].setValue(this.getParameterValues(Constants.SIEMENS_OTHER,this.costReviewContractualDocumentation?.patchingPainting));
    this.costReviewContractualDocumentationForm.controls['demolitionRemoval'].setValue(this.getParameterValues(Constants.SIEMENS_OTHER,this.costReviewContractualDocumentation?.demolitionRemoval));
    this.costReviewContractualDocumentationForm.controls['cleanUp'].setValue(this.getParameterValues(Constants.SIEMENS_OTHER,this.costReviewContractualDocumentation?.cleanUp));
    this.costReviewContractualDocumentationForm.controls['specialInsuranceCertification'].setValue(this.getParameterValues(Constants.YES_NO,this.costReviewContractualDocumentation?.specialInsuranceCertification));
    this.costReviewContractualDocumentationForm.controls['possibleChangeOrdersIdentified'].setValue(this.costReviewContractualDocumentation?.possibleChangeOrdersIdentified || Constants.EMPTY_STRING);
    this.costReviewContractualDocumentationForm.controls['performanceBondAmount'].setValue(this.costReviewContractualDocumentation?.performanceBondAmount  || Constants.ZERO);
    this.costReviewContractualDocumentationForm.controls['paymentBondAmount'].setValue(this.costReviewContractualDocumentation?.paymentBondAmount  || Constants.ZERO);
    this.costReviewContractualDocumentationForm.controls['penetrationRequirements'].setValue(this.commonFunctionsService.toNumberArray(this.costReviewContractualDocumentation?.penetrationRequirements, this.penetrationRequiredList));
    this.costReviewContractualDocumentationForm.controls['trainingSpecified'].setValue(this.getParameterValues(Constants.YES_NO,this.costReviewContractualDocumentation?.trainingSpecified));
    this.costReviewContractualDocumentationForm.controls['changeOrderMarkupRestrictions'].setValue(this.getParameterValues(Constants.YES_NO,this.costReviewContractualDocumentation?.changeOrderMarkupRestrictions));
    this.costReviewContractualDocumentationForm.controls['changeOrderOpportunities'].setValue(this.costReviewContractualDocumentation?.changeOrderOpportunities || Constants.EMPTY_STRING);
    this.costReviewContractualDocumentationForm.controls['liabilityInsurance'].setValue(this.getParameterValues(Constants.YES_NO,this.costReviewContractualDocumentation?.liabilityInsurance));
    this.costReviewContractualDocumentationForm.controls['deductionsOffered'].setValue(this.getParameterValues(Constants.YES_NO,this.costReviewContractualDocumentation?.deductionsOffered));
    this.costReviewContractualDocumentationForm.controls['licensesPermits'].setValue(this.getParameterValues(Constants.YES_NO,this.costReviewContractualDocumentation?.licensesPermits));
    this.costReviewContractualDocumentationForm.controls['specialSafetyRequirements'].setValue(this.costReviewContractualDocumentation?.specialSafetyRequirements || Constants.EMPTY_STRING);
    this.costReviewContractualDocumentationForm.controls['testingFatDates'].setValue(
      this.costReviewContractualDocumentation?.testingFromDate && this.costReviewContractualDocumentation?.testingToDate
        ? 'dates'
        : Constants.NA
    );
    this.costReviewContractualDocumentationForm.controls['testingFromDate'].setValue(
      this.formatDateValue(this.costReviewContractualDocumentation?.testingFromDate)
    );
    this.costReviewContractualDocumentationForm.controls['testingToDate'].setValue(
      this.formatDateValue(this.costReviewContractualDocumentation?.testingToDate)
    );
  }

  getParameterValues(key: string, value: string | number | undefined): string {
    return this.reviewService.getCostReviewParametersDescription(key, value)
  }

  getParameterValuesForArray(key: string, value: Array<string | number> | undefined): string {
    return this.reviewService.getCostReviewParametersDescriptionForArray(key, value);
  }

  private getParameterCode(parameter: string, description: string, currentValue: number): number {
    if(!description || description === Constants.EMPTY_STRING) {
      return 1;
    }
    const code = this.reviewService.getCostReviewParametersCode(parameter, description);
    return code !== undefined ? code : currentValue;
  }

  isSectionNotApplicableShow(): boolean {
    return !this.isSectionVisible && (this.business === this.constants.BUSINESS_FIRE || this.business === this.constants.BUSINESS_SECURITY || this.business === this.constants.BUSINESS_ELECTRICAL || this.business === this.constants.BUSINESS_AUTOMATION || this.business === this.constants.BUSINESS_MECHANICAL) && !this.showSectionNotApplicableMsg;
  }

  private exportContractualDocumentationPdf(data: any, title: string, selectedReview: ReviewDetails) {
    const options: ExportObjectAsFieldValuePDFOptions = {
      data,
      title,
      fileName: 'contractual-documentation.pdf',
      estimateID: selectedReview.estimateID,
      estimateName: selectedReview.request,
      tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.CONTRACTUAL_DOCUMENTATION'),
    };
    this.commonFunctionsService.exportObjectAsFieldValuePDF(options);

  }

  exportToPdf(selectedReview: ReviewDetails, tabName: string) {
    if (tabName === Constants.SOL_CONTRACTUAL_DOCUMENTATION) {

      if (!this.isSectionVisible || this.isSectionLocked) {
        this.exportContractualDocumentationPdf(
          { [this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.CONTRACTUAL_DOCUMENTATION_DATA_NOT_FOUND')]: Constants.EMPTY_STRING },
          Constants.EMPTY_STRING,
          selectedReview
        );
        return;
      }

      const docData: ExportTitleCaseMap = {};

      this.addAlwaysIncludedFields(docData);
      this.addSiemensTermsFields(docData);
      this.addPossibleChangeOrdersField(docData);
      this.addBondAmountFields(docData);
      this.addRightColumnFields(docData);
      this.addSpecialInsuranceFields(docData);
      this.addDeductionsTestingLicensesFields(docData);
      this.addSectionNotApplicableFields(docData);

      this.exportContractualDocumentationPdf(
        docData,
        this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.TITLE'),
        selectedReview
      );
    }
  }

  addAlwaysIncludedFields(docData: ExportTitleCaseMap) {
    if (this.isSalePriceGreaterThan150) {
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.ORIGINAL_COMPLETION_DATE')] = this.costReviewContractualDocumentation?.originalCompletionDate
        ? new Date(this.costReviewContractualDocumentation.originalCompletionDate).toLocaleDateString(this.commonDataService.selectedLocale)
        : Constants.EMPTY_STRING;
    }
    docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.SIEMENS_TERMS_AND_CONDITIONS_USED')] = this.getParameterValues(
      this.constants.YES_NO,
      this.costReviewContractualDocumentation?.siemensTermsConditionsUsed
    );
  }

  addSiemensTermsFields(docData: ExportTitleCaseMap) {
    const isSiemensTermsYes = this.commonFunctionsService.isValueEqualToTranslation(
      this.getParameterValues(this.constants.YES_NO, this.costReviewContractualDocumentation?.siemensTermsConditionsUsed), 'YES');

    if (!isSiemensTermsYes) {
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.WARRANTY_PERIOD_MONTHS')] = this.costReviewContractualDocumentation?.warrantyPeriod.toString() ?? Constants.EMPTY_STRING;
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.LIQUIDATED_DAMAGES_LIABILITY')] = this.getParameterValues(
        this.constants.LIQUIDATED_DAMAGES_LIABILITY,
        this.costReviewContractualDocumentation?.liquidatedDamagesLiability
      );
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.CONSEQUENTIAL_DAMAGES_IN_CONTRACT')] = this.getParameterValues(
        this.constants.CONSEQUENTIAL_DAMAGES_IN_CONTRACT,
        this.costReviewContractualDocumentation?.consequentialDamagesInContract
      );
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.WAIVER_REASON')] = this.getParameterValues(
        this.constants.WAIVER_REASON,
        this.costReviewContractualDocumentation?.waiverReason
      );
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.FIRE_STOPPING')] = this.getParameterValues(
        this.constants.SIEMENS_OTHER_SUBCONTRACTOR,
        this.costReviewContractualDocumentation?.fireStopping
      );
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.PATCHING_AND_PAINTING')] = this.getParameterValues(
        this.constants.SIEMENS_OTHER,
        this.costReviewContractualDocumentation?.patchingPainting
      );
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.DEMOLITION_OR_REMOVAL')] = this.getParameterValues(
        this.constants.SIEMENS_OTHER,
        this.costReviewContractualDocumentation?.demolitionRemoval
      );
      if (this.business !== this.constants.BUSINESS_SECURITY) {
        docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.CLEAN_UP')] = this.getParameterValues(
          this.constants.SIEMENS_OTHER,
          this.costReviewContractualDocumentation?.cleanUp
        );
      }
    }
  }

  addPossibleChangeOrdersField(docData: ExportTitleCaseMap) {
    if (
      this.business !== this.constants.BUSINESS_SECURITY &&
      this.business !== this.constants.BUSINESS_FIRE
    ) {
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.POSSIBLE_CHANGE_ORDERS_IDENTIFIED')] =
        this.costReviewContractualDocumentation?.possibleChangeOrdersIdentified ?? Constants.EMPTY_STRING;
    }
  }

  addBondAmountFields(docData: ExportTitleCaseMap) {
    docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.PERFORMANCE_BOND_AMOUNT')] = this.costReviewContractualDocumentation?.performanceBondAmount.toString() ?? Constants.EMPTY_STRING;
    docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.PAYMENT_BOND_AMOUNT')] = this.costReviewContractualDocumentation?.paymentBondAmount.toString() ?? Constants.EMPTY_STRING;
  }

  addRightColumnFields(docData: ExportTitleCaseMap) {
    docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.PENETRATION_REQUIREMENTS')] = this.getParameterValuesForArray(
      this.constants.PENETRATION_REQUIREMENTS,
      this.costReviewContractualDocumentation?.penetrationRequirements
    );
    docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.TRAINING_SPECIFIED')] = this.getParameterValues(
      this.constants.YES_NO,
      this.costReviewContractualDocumentation?.trainingSpecified
    );
    docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.CHANGE_ORDER_MARKUP_RESTRICTIONS')] = this.getParameterValues(
      this.constants.YES_NO,
      this.costReviewContractualDocumentation?.changeOrderMarkupRestrictions
    );
    docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.CHANGE_ORDER_OPPORTUNITIES')] =
      this.costReviewContractualDocumentation?.changeOrderOpportunities ?? Constants.EMPTY_STRING;

    docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.PERFORMANCE_BOND_AMOUNT')] = this.formatDollar(this.costReviewContractualDocumentation?.performanceBondAmount);
    docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.PAYMENT_BOND_AMOUNT')] = this.formatDollar(this.costReviewContractualDocumentation?.paymentBondAmount);
  }

  addSpecialInsuranceFields(docData: ExportTitleCaseMap) {
    if (this.business !== this.constants.BUSINESS_SECURITY && this.isSalePriceGreaterThan150) {
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.SPECIAL_INSURANCE_CERTIFICATION')] = this.getParameterValues(
        this.constants.YES_NO,
        this.costReviewContractualDocumentation?.specialInsuranceCertification
      );
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.LIABILITY_INSURANCE_SPECIAL_COVERAGE')] = this.getParameterValues(
        this.constants.YES_NO,
        this.costReviewContractualDocumentation?.liabilityInsurance
      );
    }
  }

  addDeductionsTestingLicensesFields(docData: ExportTitleCaseMap) {
    if (this.isSalePriceGreaterThan150) {
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.DEDUCTIONS_OFFERED')] = this.getParameterValues(
        this.constants.YES_NO,
        this.costReviewContractualDocumentation?.deductionsOffered
      );

      if (
        this.costReviewContractualDocumentation?.testingFromDate &&
        this.costReviewContractualDocumentation?.testingToDate
      ) {
        docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.TESTING_PERIOD_FAT')] =
          'From: ' +
          new Date(this.costReviewContractualDocumentation.testingFromDate).toLocaleDateString(this.commonDataService.selectedLocale) +
          ' To: ' +
          new Date(this.costReviewContractualDocumentation.testingToDate).toLocaleDateString(this.commonDataService.selectedLocale);
      } else {
        docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.TESTING_PERIOD_FAT')] = this.constants.NA;
      }

      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.LICENSES_PERMITS')] = this.getParameterValues(
        this.constants.YES_NO,
        this.costReviewContractualDocumentation?.licensesPermits
      );
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.SPECIAL_SAFETY_REQUIREMENTS')] =
        this.costReviewContractualDocumentation?.specialSafetyRequirements ?? Constants.EMPTY_STRING;
    }
  }

  addSectionNotApplicableFields(docData: ExportTitleCaseMap) {
    if (this.isSectionNotApplicableShow && this.isSectionNotApplicableShow()) {
      docData[this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.SECTION_NOT_APPLICABLE')] = Constants.EMPTY_STRING;
    }

    if (
      this.showSectionNotApplicableMsg &&
      this.costReviewContractualDocumentation &&
      this.isSectionLocked
    ) {
      docData[this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.CONTRACTUAL_DOCUMENTATION_DATA_NOT_FOUND')] = Constants.EMPTY_STRING;
    }
  }

  formatDollar(value: string | number | undefined): string {
    if (value === undefined || value === null || value === Constants.EMPTY_STRING) return Constants.EMPTY_STRING;
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num)) return String(value);
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: Constants.TWO, maximumFractionDigits: Constants.TWO })}`;
  }

  get isSiemensTermsYes(): boolean {
    return this.commonFunctionsService.isControlValueEqualToTranslation(this.costReviewContractualDocumentationForm, 'siemensTermsConditionsUsed', 'YES') || this.isDisabled;
  }

  get isTestingDatesDisabled() : boolean {
    return this.costReviewContractualDocumentationForm.get('testingFatDates')?.value === this.constants.NA;
  }
  get testingFromDateValue(): string {
    return this.costReviewContractualDocumentationForm.get('testingFromDate')?.value || this.constants.EMPTY_STRING;
  }

  getValueInString(formControlName: string): string {
    return this.costReviewContractualDocumentationForm.get(formControlName)?.value || this.constants.EMPTY_STRING;
  }

  onTestingDateChange(controlName: 'testingFromDate' | 'testingToDate', value: string): void {
    this.costReviewContractualDocumentationForm.controls[controlName].setValue(value || Constants.EMPTY_STRING);
  }

  private getReviewLibraryState(): void {
    this.isDisabled = this.reviewDetailsTabService.getReviewLibraryDisableFlag();
  }
  private mapPenetrationRequirements(ids: number[] | undefined): string[] {
    if(!ids?.length) return [];
    return ids
    .map(id => {
      const desc = this.reviewService.getCostReviewParametersDescription(Constants.PENETRATION_REQUIREMENTS, id);
      return this.penetrationRequiredList.find(key => this.translate.instant(key) === desc);
    })
    .filter((key): key is string => !!key);
  }

}
